import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SERVERURL } from './constant';

export interface AppNotification {
  id: string;
  type: string;
  notifiable_type: string;
  notifiable_id: number;
  data: {
    title: string;
    message: string;
    url?: string;
    icon?: string;
  };
  read_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationResponse {
  unread_count: number;
  notifications: AppNotification[];
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private unreadMessageCountSubject = new BehaviorSubject<number>(0);
  public unreadMessageCount$ = this.unreadMessageCountSubject.asObservable();

  private pollingInterval: any;
  private userId: number | null = null;
  private api = SERVERURL + '/api/';

  constructor(private http: HttpClient) {}

  public init(userId: number) {
    this.userId = userId;
    this.fetchNotifications();
    
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    this.pollingInterval = setInterval(() => {
      this.fetchNotifications();
    }, 5000);
  }

  public stop() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }
    this.userId = null;
  }

  public fetchNotifications() {
    if (!this.userId) return;

    this.http.get<any>(`${this.api}notifications/client/${this.userId}`).subscribe({
      next: (res) => {
        this.notificationsSubject.next(res.notifications);
        this.unreadCountSubject.next(res.unread_count);
        this.unreadMessageCountSubject.next(res.unread_message_count || 0);
      },
      error: (err) => console.error('Error fetching notifications', err)
    });
  }

  public markAsRead(notificationId: string) {
    if (!this.userId) return throwError('No user id');
    return this.http.post(`${this.api}notifications/client/${notificationId}/read/${this.userId}`, {}).pipe(
      tap(() => this.fetchNotifications())
    );
  }

  public markAllAsRead() {
    if (!this.userId) return throwError('No user id');
    return this.http.post(`${this.api}notifications/client/read-all/${this.userId}`, {}).pipe(
      tap(() => {
        this.unreadCountSubject.next(0);
        const current = this.notificationsSubject.value;
        const updated = current.map(n => ({ ...n, read_at: new Date().toISOString() }));
        this.notificationsSubject.next(updated);
        this.fetchNotifications();
      })
    );
  }

  public deleteNotification(notificationId: string) {
    if (!this.userId) return throwError('No user id');
    return this.http.delete(`${this.api}notifications/client/${notificationId}/delete/${this.userId}`).pipe(
      tap(() => this.fetchNotifications())
    );
  }

  public deleteAll() {
    if (!this.userId) return throwError('No user id');
    return this.http.delete(`${this.api}notifications/client/delete-all/${this.userId}`).pipe(
      tap(() => {
        this.notificationsSubject.next([]);
        this.unreadCountSubject.next(0);
      })
    );
  }
}
